// frontend/src/pages/Gns3NodesAdmin.jsx
// Gestion des nodes GNS3 pour admin/super_admin
import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, LinearProgress,
  Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

import api from "../api/axios";
import { useTranslation } from "react-i18next";

// ── Helpers ──────────────────────────────────────────────────────────
function StatusChip({ healthy, failures }) {
  return healthy ? (
    <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" size="small" />
  ) : (
    <Chip icon={<ErrorIcon />} label={`Down (${failures} fails)`} color="error" size="small" />
  );
}

function LoadBar({ pct }) {
  const color = pct >= 80 ? "error" : pct >= 50 ? "warning" : "success";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ flex: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption" color="text.secondary">{pct}%</Typography>
    </Box>
  );
}

// ── Page principale ───────────────────────────────────────────────────
export default function Gns3NodesAdmin() {
  const { t } = useTranslation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState({});
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [addOpen, setAddOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({
    id: "", url: "", user: "admin", password: "admin",
    weight: 1, max_projects: 20,
  });

  const notify = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  // ── Charger le statut du pool ─────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await api.get("/admin/gns3/nodes/status");
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur chargement statut GNS3");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // ── Health check manuel ───────────────────────────────────────────
  const checkNode = async (nodeId) => {
    setChecking(prev => ({ ...prev, [nodeId]: true }));
    try {
      await api.post(`/admin/gns3/nodes/${nodeId}/check`);
      notify(`Node ${nodeId} vérifié`);
      await loadStatus();
    } catch (e) {
      notify(`Erreur health check: ${e?.response?.data?.detail || e.message}`, "error");
    } finally {
      setChecking(prev => ({ ...prev, [nodeId]: false }));
    }
  };

  // ── Ajouter un node ───────────────────────────────────────────────
  const addNode = async () => {
    try {
      await api.post("/admin/gns3/nodes", {
        id: form.id,
        url: form.url,
        user: form.user,
        password: form.password,
        weight: parseInt(form.weight),
        max_projects: parseInt(form.max_projects),
      });
      notify(`Node "${form.id}" ajouté`);
      setAddOpen(false);
      setForm({ id: "", url: "", user: "admin", password: "admin", weight: 1, max_projects: 20 });
      await loadStatus();
    } catch (e) {
      notify(e?.response?.data?.detail || "Erreur ajout node", "error");
    }
  };

  // ── Supprimer un node ─────────────────────────────────────────────
  const deleteNode = async (nodeId) => {
    try {
      await api.delete(`/admin/gns3/nodes/${nodeId}`);
      notify(`Node "${nodeId}" supprimé`);
      setDeleteConfirm(null);
      await loadStatus();
    } catch (e) {
      notify(e?.response?.data?.detail || "Erreur suppression", "error");
    }
  };

  const nodes = status?.nodes || [];

  return (
    <Stack spacing={3} sx={{ maxWidth: 1100, mx: "auto", my: 3, px: 2 }}>

      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography variant="h5" fontWeight={600}>🖧 Serveurs GNS3</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestion du pool de serveurs GNS3 — ajout, suppression, health check
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            onClick={loadStatus}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setAddOpen(true)}
          >
            Ajouter un node
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

      {/* ── KPI Cards ── */}
      {status && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <KpiCard label="Nodes total" value={status.total_nodes} color="default" />
          <KpiCard label="Nodes sains" value={status.healthy_nodes} color="success" />
          <KpiCard label="Slots disponibles" value={status.total_available_slots} color="info" />
          <KpiCard
            label="Stratégie LB"
            value={status.strategy?.replace("_", " ")}
            color="default"
            isText
          />
        </Stack>
      )}

      {/* ── Table des nodes ── */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Nodes du pool</Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : nodes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">Aucun node configuré</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell><b>ID</b></TableCell>
                <TableCell><b>URL</b></TableCell>
                <TableCell><b>Statut</b></TableCell>
                <TableCell><b>Version</b></TableCell>
                <TableCell><b>Charge</b></TableCell>
                <TableCell><b>Slots</b></TableCell>
                <TableCell><b>Weight</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nodes.map((node) => (
                <TableRow key={node.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {node.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                      {node.url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip healthy={node.is_healthy} failures={node.failures} />
                  </TableCell>
                  <TableCell>
                    <Chip label={node.version || "—"} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <LoadBar pct={node.load_pct} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {node.available_slots} / {node.max_projects}
                    </Typography>
                  </TableCell>
                  <TableCell>{node.weight}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Health check">
                        <IconButton
                          size="small"
                          onClick={() => checkNode(node.id)}
                          disabled={checking[node.id]}
                          color="success"
                        >
                          {checking[node.id]
                            ? <CircularProgress size={16} />
                            : <HealthAndSafetyIcon fontSize="small" />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm(node)}
                          color="error"
                          disabled={node.id === "default"}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Dialog ajout node ── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un serveur GNS3</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="ID unique" size="small" fullWidth required
              value={form.id}
              onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
              placeholder="node2"
            />
            <TextField
              label="URL" size="small" fullWidth required
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="http://192.168.1.10:80"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Utilisateur" size="small" fullWidth
                value={form.user}
                onChange={e => setForm(f => ({ ...f, user: e.target.value }))}
              />
              <TextField
                label="Mot de passe" size="small" fullWidth type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Weight" size="small" type="number" fullWidth
                value={form.weight}
                onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
                helperText="Priorité relative (1-10)"
              />
              <TextField
                label="Max projets" size="small" type="number" fullWidth
                value={form.max_projects}
                onChange={e => setForm(f => ({ ...f, max_projects: e.target.value }))}
                helperText="Capacité maximale"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={addNode}
            disabled={!form.id || !form.url}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog confirmation suppression ── */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Supprimer le node</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmer la suppression du node <b>{deleteConfirm?.id}</b> ({deleteConfirm?.url}) ?
          </Typography>
          {deleteConfirm?.active_projects > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Ce node a {deleteConfirm.active_projects} projet(s) actif(s) !
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteNode(deleteConfirm.id)}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function KpiCard({ label, value, color, isText }) {
  return (
    <Paper sx={{ p: 2, flex: 1, borderRadius: 2 }} elevation={0} variant="outlined">
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Box sx={{ mt: 0.5 }}>
        {isText
          ? <Chip label={value} size="medium" variant="outlined" />
          : <Chip label={value} color={color} size="medium" />
        }
      </Box>
    </Paper>
  );
}
